import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';

import { parseReportSearch, type ReportSearch } from '../../features/reports/range';
import { IntegrationsReportScreen } from '../../features/reports/report-screens';
import { REPORT_PERMISSIONS } from '../../features/reports/types';
import { RequirePermission } from '../../lib/session/guards';
import { usePermissions } from '../../lib/session/permissions';

export const Route = createFileRoute('/reports/integrations')({
  validateSearch: (search: Record<string, unknown>): ReportSearch => parseReportSearch(search),
  component: IntegrationsReportPage,
});

/**
 * `/reports/integrations` — vendor health over the range (FR-REPORT-04).
 *
 * `reports.integrations` opens it. The route is `/reports/integrations` because that is
 * what the screen is called; the endpoint behind it is `/admin/reports/integration-health`
 * (API.md §2.11), and `lib/api/reports.ts` is the one place those two names are mapped.
 *
 * Each row links to `/integrations/$key`, which is the same vendor with its credentials,
 * its settings and its own call log — the report says something is wrong, that screen is
 * where it gets fixed.
 */
function IntegrationsReportPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const now = useMemo(() => new Date(), []);
  const { can } = usePermissions();

  return (
    <RequirePermission keys={[REPORT_PERMISSIONS['integration-health']]}>
      <IntegrationsReportScreen
        search={search}
        onSearchChange={(next) => {
          void navigate({ to: '/reports/integrations', search: next, replace: true });
        }}
        now={now}
        can={can}
        backLink={
          <Link to="/reports" search={search} className="text-primary-accessible hover:underline">
            Reports
          </Link>
        }
        renderIntegrationLink={(key, children) => (
          <Link
            to="/integrations/$key"
            params={{ key }}
            className="font-medium text-primary-accessible hover:underline"
          >
            {children}
          </Link>
        )}
      />
    </RequirePermission>
  );
}
